import * as React from 'react'
import { Bar, Cell, BarChart as RechartsBarChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts'

import ChartTooltip from './ChartTooltip'
import {
  BAR_CHART_LABELS_MARGIN,
  BAR_CHART_MARGINS,
  BAR_GAP,
  BAR_WIDTH,
  CHART_HEIGHT,
  CURSOR_STYLE,
  EXTRA_WIDTH,
  LABELS_WIDTH,
  TICK_STYLE,
  TOOLTIP_WRAPPER_STYLE,
  TRANSPARENT_CURSOR,
} from './constants'
import { ChartProps } from './types'
import { useTooltipActiveStateControls } from './useTooltipActiveStateControls'
import { getChartDataItemKey, tickFormatter } from './utils'

export default function BarChart({ chartData, visibleAreas, columnsCount, isFullWidth }: ChartProps) {
  const { isActive, chartWrapperActiveStateControlsProps } = useTooltipActiveStateControls()
  const [activeBarKey, setActiveBarKey] = React.useState('')
  const [reverseTooltipDirection, setReverseTooltipDirection] = React.useState(true)
  const chartWidth = columnsCount * BAR_WIDTH + (columnsCount - 1) * BAR_GAP + EXTRA_WIDTH

  const ChartWrapper = isFullWidth ? ResponsiveContainer : React.Fragment

  return (
    <div
      {...(isFullWidth ? { className: 'w-full', style: { height: CHART_HEIGHT } } : {})}
      {...chartWrapperActiveStateControlsProps}
    >
      <ChartWrapper>
        <RechartsBarChart
          width={chartWidth}
          height={CHART_HEIGHT}
          data={chartData}
          margin={BAR_CHART_MARGINS}
          barCategoryGap={BAR_GAP / 2}
          stackOffset="sign" // because we can have negative values
          onMouseMove={(_, mouseEvent) => {
            setReverseTooltipDirection(mouseEvent.clientX > window.innerWidth / 2)
          }}
        >
          <YAxis
            className="font-sans text-muted-2"
            orientation="right"
            tick={TICK_STYLE}
            tickLine={false}
            axisLine={false}
            tickFormatter={tickFormatter}
            tickMargin={BAR_CHART_LABELS_MARGIN}
            width={LABELS_WIDTH}
            interval="preserveStartEnd"
          />
          <Tooltip
            wrapperStyle={TOOLTIP_WRAPPER_STYLE}
            position={{ y: 0 }}
            allowEscapeViewBox={{ x: true }}
            reverseDirection={{ x: reverseTooltipDirection }}
            offset={40}
            isAnimationActive={false}
            cursor={isActive ? CURSOR_STYLE : TRANSPARENT_CURSOR}
            content={({ payload }) => (
              <ChartTooltip isActive={isActive} activeKey={activeBarKey} payload={payload} isReversed />
            )}
          />
          {/* Don't change the order of mapped items here, see getChartData */}
          {visibleAreas.map((area) => {
            const itemKey = getChartDataItemKey(area)
            return (
              <Bar
                key={itemKey}
                name={area.label}
                dataKey={itemKey}
                stackId="year"
                fill={area.color}
                isAnimationActive={false}
                onMouseEnter={() => {
                  setActiveBarKey(itemKey)
                }}
                onMouseLeave={() => {
                  setActiveBarKey('')
                }}
              >
                {chartData.map((row, index) => (
                  <Cell
                    key={index}
                    className="stroke-white stroke-[0.25px] dark:stroke-gray-800"
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore - number[] should also be allowed type for radius, but it's not
                    radius={[row.topKey, row.bottomKey].includes(itemKey) ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </Bar>
            )
          })}
        </RechartsBarChart>
      </ChartWrapper>
    </div>
  )
}
